/**
 * API Field ID Constants
 * 
 * Centralized constants for API data field identifiers.
 * This provides a single source of truth for all API field IDs,
 * enables auto-completion, and allows the compiler to catch typos.
 * 
 * Usage:
 * import { API_FIELD_IDS } from '@/lib/constants/apiFieldIds';
 * const title = getValue(API_FIELD_IDS.TITLE_DEVICE_STATUS) || "Device Status";
 */

export const API_FIELD_IDS = Object.freeze({
  // Common Fields
  PAGE_TITLE: "page_title",
  PAGE_DESCRIPTION: "page_description",
  CUSTOMER_SUPPORT_CONTACTS: "customer_support_contacts",
  ELIGIBLE_REASON_CODE: "eligible_reason_code",
  ELIGIBLE_REASON_CODE_1: "eligible_reason_code1",
  ELIGIBLE_REASON_CODE_2: "eligible_reason_code2",
  
  // Device Status Fields
  TITLE_DEVICE_STATUS: "title_devicestatus",
  STATUS_ID: "status_id",
  STATUS_VALUE: "status_value",
  DEVICE_ID: "device_id",
  DEVICE_ID_VALUE: "deviceid_value",
  REASON_ID: "reason_id",
  REASON_DESCRIPTION_DEVICE_STATUS: "reasondes_devicestatus",

  // Device Status Below Text (Eligibility)
  DEVICE_STATUS_BELOW_TEXT_1: "devicestatus_belowtext1",
  DEVICE_STATUS_BELOW_TEXT_2: "devicestatus_belowtext2",
  DEVICE_STATUS_BELOW_TEXT_3: "devicestatus_belowtext3",
  DEVICE_STATUS_BELOW_TEXT_4: "devicestatus_belowtext4",
  DEVICE_STATUS_BELOW_TEXT_5: "devicestatus_belowtext5",

  // Eligibility Fields
  ELIGIBILITY_LIST: "eligibility_list",
  TEXT_INTRO: "text_intro",
  TEXT_LIST_CRITERIA: "text_list_criteria",
  FIRST_NAME_LABEL: "first_name_label",
  LAST_NAME_LABEL: "last_name_label",
  EMAIL_LABEL: "email_label",
  CONTACT_PHONE_LABEL: "contact_phone_label",
  FIRST_NAME_PLACEHOLDER: "first_name_placeholder",
  LAST_NAME_PLACEHOLDER: "last_name_placeholder",
  EMAIL_PLACEHOLDER: "email_placeholder",
  CONTACT_PHONE_PLACEHOLDER: "contact_phone_placeholder",
  UNLOCK_PHONE_TEXT: "unlock_phone_text",
  FIRST_NAME_REQUIRED: "first_name_required",
  LAST_NAME_REQUIRED: "last_name_required",
  EMAIL_REQUIRED: "email_required",
  EMAIL_INVALID: "email_invalid",
  PHONE_REQUIRED: "phone_required",
  PHONE_INVALID: "phone_invalid",
  SUBMIT_CTA: "submit_cta",
  BACK_CTA: "back_cta",

  // Brand/Carrier Text Fields
  TEXT_TITLE_BRANDS: "text_title_brands",
  TEXT_LIST_BRANDS: "text_list_brands",
  TEXT_TITLE_SAFELINK: "text_title_safelink",
  TEXT_LIST_SAFELINK: "text_list_safelink",

  // Note Fields
  TEXT_NOTE_LABEL: "text_note_label",
  TEXT_NOTE_BODY: "text_note_body",

  // Unlock Instructions
  UNLOCK_INSTRUCTIONS_TITLE_1: "unlock_instructions_title_1",
  UNLOCK_INSTRUCTIONS_TEXT_1: "unlock_instructions_text_1",
  UNLOCK_INSTRUCTIONS_TITLE_2: "unlock_instructions_title_2",
  UNLOCK_INSTRUCTIONS_TEXT_2: "unlock_instructions_text_2",

  // Order Summary Fields
  TITLE_ORDER_SUMMARY: "title_ordersummary",
  DESCRIPTION_ORDER_SUMMARY: "des_ordersummary",

  // Contact Information Fields
  PHONE_NUMBER_ID: "phonenumber_id",
  PHONE_NUMBER_VALUE: "phonenumber_value",
  ID_SERIAL_NUMBER: "id_serialnumber",
  SERIAL_NUMBER_VALUE: "serialnumber_value",
  ID_TICKET_NUMBER: "id_ticketnumber",
  TICKET_NUMBER_VALUE: "ticketnumber_value",
  ID_EMAIL_ADDRESS: "id_emailaddress",
  EMAIL_ADDRESS_VALUE_ID: "emailaddressvalue_id",

  // Instructions Fields
  INSTRUCTIONS_HEADING: "instructions_herding",
  INSTRUCTIONS_PARAGRAPH: "instructions_paragraph",
  INSTRUCTIONS_PARAGRAPH_2: "instructions_paragraph2",
  INSTRUCTIONS_PARAGRAPH_3: "instructions_paragraph3",
  UNLOCK_CODE_BELOW_TEXT: "unlockcode_belowtext",
  UNLOCK_CODE_BELOW_TEXT_1: "unlockcode_belowtext1",
  UNLOCK_CODE_BELOW_TEXT_2: "unlockcode_belowtext2",
  DEVICE_NOTE: "device_note",

  // Unlock Code Fields
  UNLOCK_CODE_V1: "unlockcode_v1",

  // Home Page Fields
  TITLE_TEXT: "title_text",
  TITLE_DESCRIPTION: "title_description", 
  LINK_TEXT: "link_text",
  PREFIX_LINK_TEXT: "prefix_link_text",
  TITLE_UNLOCKING_POLICY: "title_unlockingpolicy",
  CHECK_STATUS_CTA: "check_status_cta",
  CLOSE_CTA: "close_cta",
  INPUT_TEXT: "input_text",
  IMEI_FIELD_REQUIRED: "imei_field_required",
  IMEI_FIELD_INVALID: "imei_field_invalid",
  IMEI_TOOL_TIP_TITLE: "imei_tooltip_title",
  DESCRIPTION_IMEI_TOOLTIP1: "description_imei_tooltip1",
  DESCRIPTION_IMEI_TOOLTIP2: "description_imei_tooltip2",
  DESCRIPTION_IMEI_TOOLTIP3: "description_imei_tooltip3",
  DESCRIPTION_IMEI_TOOLTIP4: "description_imei_tooltip4",
  
  // Unlocking Policy Description Fields (1-8)
  DESCRIPTION_UNLOCKING_POLICY_1: "description_unlockingpolicy1",
  DESCRIPTION_UNLOCKING_POLICY_2: "description_unlockingpolicy2", 
  DESCRIPTION_UNLOCKING_POLICY_3: "description_unlockingpolicy3",
  DESCRIPTION_UNLOCKING_POLICY_4: "description_unlockingpolicy4",
  DESCRIPTION_UNLOCKING_POLICY_5: "description_unlockingpolicy5",
  DESCRIPTION_UNLOCKING_POLICY_6: "description_unlockingpolicy6",
  DESCRIPTION_UNLOCKING_POLICY_7: "description_unlockingpolicy7",
  DESCRIPTION_UNLOCKING_POLICY_8: "description_unlockingpolicy8",
} as const);

// Type for API Field IDs (for type safety)
export type ApiFieldId = typeof API_FIELD_IDS[keyof typeof API_FIELD_IDS];

/**
 * Helper function to get all unlocking policy description field IDs
 * @param count Number of policy description fields (default: 8)
 * @returns Array of field IDs for unlocking policy descriptions
 */
export const getUnlockingPolicyFieldIds = (count: number = 8): string[] => {
  return Array.from({ length: count }, (_, i) => {
    const fieldKey = `DESCRIPTION_UNLOCKING_POLICY_${i + 1}` as keyof typeof API_FIELD_IDS;
    return API_FIELD_IDS[fieldKey];
  });
};
